/**
 * paid-3 (THINK™ — cognitive signal) results view-model + builder.
 *
 * Source: the live cognitive results array (`scan.cognitive`, SHARP-v2). The
 * composite is the SHARP index (ScoreCalculationService.calculateCompositeScore);
 * each lens score is the real per-task score (calculateSharpV2TaskScore). No
 * placeholders. When the cognitive module wasn't completed, `available:false`
 * and the panel shows a take-the-test state.
 */
import { Band } from '../../results-vm';
import { higherBetterBand, CohortAverages } from '../../results-placeholders';

export interface ThinkTaskVM {
  key: string;
  titleKey: string;
  subtitleKey: string;
  whatKey: string;
  pct: number; // 0–100, higher = stronger
  color: string;
  labelKey: string;
  // Step2 change5/6: an explanatory sentence shown beside a tile's score (NOT a
  // score/band/colour). recallInsight → the recall tile, wmInsight → the working-
  // memory tile. Absent on every other tile.
  recallInsight?: RecallInsight;
  wmInsight?: WmInsight;
}

/** Step2 change5: cue-benefit sentence for the delayed-recall tile. `free`/`cued`/
 *  `missed` are item-field keys (name|color|object|place); the panel translates
 *  and joins them per language. */
export interface RecallInsight {
  key: string;
  free: string[];
  cued: string[];
  missed: string[];
}

/** Step2 change6: backward-cost sentence + numeric caption for the working-memory tile. */
export interface WmInsight {
  key: string;
  forward: number;
  backward: number;
}

const RECALL_FIELDS = ['name', 'color', 'object', 'place'];

function normStr(s: any): string {
  return (s ?? '').toString().trim().toLowerCase();
}

/** Legacy fallback when a scan predates the persisted freeItems/cuedItems arrays:
 *  approximate the free/cued item sets from the raw responses (exact for
 *  colour/place, lenient prefix match for name/object). */
function deriveFreeItems(ms: any): string[] {
  const items = ms?.recallItems;
  const fr = ms?.freeResponses;
  if (!items || !fr) return [];
  return RECALL_FIELDS.filter((k) => {
    const a = normStr(fr[k]);
    const b = normStr(items[k]);
    if (!a || !b) return false;
    return (
      a === b ||
      (b.startsWith(a) && b.length - a.length <= 2) ||
      (a.startsWith(b) && a.length - b.length <= 1)
    );
  });
}
function deriveCuedItems(ms: any, free: string[]): string[] {
  const items = ms?.recallItems;
  const cr = ms?.cuedResponses || {};
  if (!items) return [];
  return RECALL_FIELDS.filter(
    (k) => !free.includes(k) && cr[k] != null && normStr(cr[k]) === normStr(items[k])
  );
}

function buildRecallInsight(ms: any): RecallInsight {
  const free = Array.isArray(ms?.freeItems) ? ms.freeItems : deriveFreeItems(ms);
  const cued = Array.isArray(ms?.cuedItems) ? ms.cuedItems : deriveCuedItems(ms, free);
  const missed = RECALL_FIELDS.filter((k) => !free.includes(k) && !cued.includes(k));
  const freeScore = typeof ms?.freeScore === 'number' ? ms.freeScore : free.length;
  const cueBenefit =
    typeof ms?.cueBenefit === 'number' ? ms.cueBenefit : cued.length;
  let key: string;
  if (freeScore >= 3) key = 'results.th.recallInsight.strong';
  else if (cueBenefit >= 2) key = 'results.th.recallInsight.tipOfTongue';
  else if (cueBenefit === 1) key = 'results.th.recallInsight.oneHint';
  else if (cueBenefit === 0 && freeScore > 0)
    key = 'results.th.recallInsight.notSettled';
  else key = 'results.th.recallInsight.blank';
  return { key, free, cued, missed };
}

function buildWmInsight(echo: any): WmInsight {
  const f = typeof echo?.maxForward === 'number' ? echo.maxForward : 0;
  const b = typeof echo?.maxBackward === 'number' ? echo.maxBackward : 0;
  const cost =
    typeof echo?.backwardCost === 'number' ? echo.backwardCost : f - b;
  let key: string;
  if (cost <= 0) key = 'results.th.wmInsight.noCost';
  else if (cost <= 2) key = 'results.th.wmInsight.slightCost';
  else key = 'results.th.wmInsight.highCost';
  return { key, forward: f, backward: b };
}

export interface ThinkPanelVM {
  available: boolean;
  composite: number | null;
  band: Band;
  headlineKey: string;
  subCopyKey: string;
  weakestTitleKey: string | null;
  tasks: ThinkTaskVM[];
}

/** Raw per-task input (testId + real 0–100 score) computed by the VM service. */
export interface ThinkTaskInput {
  testId: string;
  score: number;
}

const COLOR_STRONG = '#3a9e95';
const COLOR_MID = '#c48a2c';
const COLOR_LOW = '#c45a4a';

/** testId → display copy keys + order. Mirrors the cognitive flow. */
const TASKS: Record<string, { key: string; order: number }> = {
  reaction: { key: 'speed', order: 1 },
  'word-garden': { key: 'words', order: 2 },
  'echo-chamber': { key: 'memory', order: 3 },
  'memory-scene': { key: 'recall', order: 4 },
  'shape-sorter': { key: 'flexibility', order: 5 },
  'vigilance-pulse': { key: 'attention', order: 6 },
};

const toneColor = (pct: number) =>
  pct >= 70 ? COLOR_STRONG : pct >= 45 ? COLOR_MID : COLOR_LOW;
/** B8 (Stage-1 wellness copy): on a FIRST check-in there is no comparable
 * history yet, so every tile reads a neutral "Your starting point" instead of
 * a comparative band ("Among the strongest" / "Worth tracking") that implies a
 * concern level. Comparative bands return once a repeat check exists. */
const bandLabelKey = (pct: number, isFirstCheck = false) =>
  isFirstCheck
    ? 'results.th.bandFirst'
    : pct >= 70
    ? 'results.th.bandStrong'
    : pct >= 45
    ? 'results.th.bandMid'
    : 'results.th.bandLow';

export function buildThinkVM(
  composite: number | null,
  taskInputs: ThinkTaskInput[],
  cohort: CohortAverages | null,
  showComparisons = true,
  raw: { memoryScene?: any; echo?: any } = {},
  isFirstCheck = false
): ThinkPanelVM {
  const tasks: ThinkTaskVM[] = (taskInputs || [])
    .filter((t) => TASKS[t.testId] && typeof t.score === 'number')
    .sort((a, b) => TASKS[a.testId].order - TASKS[b.testId].order)
    .map((t) => {
      const key = TASKS[t.testId].key;
      const pct = Math.round(Math.max(0, Math.min(100, t.score)));
      const vm: ThinkTaskVM = {
        key,
        titleKey: `results.th.tasks.${key}.title`,
        subtitleKey: `results.th.tasks.${key}.subtitle`,
        whatKey: `results.th.tasks.${key}.what`,
        pct,
        color: toneColor(pct),
        labelKey: bandLabelKey(pct, isFirstCheck),
      };
      // Step2 change5/6: attach the explanatory sentence to its tile.
      if (key === 'recall' && raw.memoryScene)
        vm.recallInsight = buildRecallInsight(raw.memoryScene);
      if (key === 'memory' && raw.echo) vm.wmInsight = buildWmInsight(raw.echo);
      return vm;
    });

  // Completion = the SAME single source of truth the overview card uses: a real
  // composite score exists. The overview marks THINK done on `composite != null`,
  // so the detail panel must too — gating on `tasks.length` as well made the
  // detail say "not completed yet" while the overview showed the score
  // (BUG-RESULTS-COMPLETION-DESYNC). The per-task breakdown below is supplementary
  // and renders only when present; it never decides completion.
  const available = composite != null;

  let weakestTitleKey: string | null = null;
  if (tasks.length) {
    weakestTitleKey = tasks.reduce((a, b) => (b.pct < a.pct ? b : a)).titleKey;
  }

  // Step2 change4: the composite headline uses the SAME 70/50 band as the
  // composite card (higherBetterBand) — one band system for the overall score, so
  // the card and the headline never disagree on the same number (was 85/65/40 here
  // vs 70/50 on the card, e.g. 78 read "strong" on the card but "mild" here). The
  // per-task tile band (70/45) stays separate — it describes a single task.
  const c = composite ?? 0;
  const headlineBand = c >= 70 ? 'strong' : c >= 50 ? 'moderate' : 'low';
  // No per-task breakdown -> no lowest task to name; use the sentence without it
  // so the copy never renders an empty {{domain}}.
  const headlineKey = weakestTitleKey
    ? `results.th.headline.${headlineBand}`
    : `results.th.headline.${headlineBand}NoDomain`;

  // Cohort comparison rail removed from THINK (design.md within-person; shipped
  // wordfirst 9ג). The cognitive cohort average was already hidden as noise.
  return {
    available,
    composite: composite != null ? Math.round(composite) : null,
    band: higherBetterBand(c),
    headlineKey,
    subCopyKey: 'results.th.subCopy',
    weakestTitleKey,
    tasks,
  };
}
